import angular from 'angular'
import simkey from 'simkey'
import simulateEvent from 'simulate-event'

import { over, uniqueId } from 'lodash-es'

import envRaw from '../utils/env-raw'
import StrictObj from '../utils/strict-obj'
import SumEmitter from '../utils/sum-emitter'
import attachGetters from '../utils/attach-getters'

import Template from './gallery-vote-frame.component.ejs'

angular
  .module('ggs.gallery-vote-frame')
  .component('ggsGalleryVoteFrame', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode
    })),
    controller: GalleryVoteFrameController,
    bindings: {
      ggsPost: '<',
      ggsProperties: '<',
      ggsVoteChangeHandler: '<',
      ggsVoteCountHideHandler: '<',
      ggsVotingDisallowHandler: '<',
      ggsVoteFrameAttachCallback: '<'
    }
  })

const QS_SPINNER = '.ggs-gallery-vote-frame__spinner'
const QS_IFRAME_PARENT = '.ggs-gallery-vote-frame__iframe-parent'

const PARENT_ITEM_CHANGE = 'parent:item:change'
const PARENT_PROPERTIES_CHANGE = 'parent:properties:change'
const PARENT_REINSTANCE_SIGNAL = 'parent:reinstance:signal'
const PARENT_HIDE_POPUPS_REQUEST = 'parent:hide-popups:request'

const FRAME_VOTE_CHANGE = 'frame:vote:change:'
const FRAME_VOTE_COUNT_HIDE = 'frame:vote-count:hide'
const FRAME_VOTING_DISALLOW = 'frame:voting:disallow'
const FRAME_KEYDOWN_TRIGGER = 'frame:keydown:trigger'
const FRAME_REINSTANCE_SIGNAL = 'frame:reinstance:signal'
const FRAME_LEFTCLICK_TRIGGER = 'frame:leftclick:trigger'
const FRAME_SEND_DATA_REQUEST = 'frame:send-data:request'

function GalleryVoteFrameController(
  $scope,
  $element,
  qh,
  domh,
  animate,
  VoteFrame,
  DefineMethod,
  DefineHandler,
  reinstanceSignalEmitter) {

  const sign = `galleryVoteFrame[uid=${uniqueId()}]`
  const $ctrl = $scope.$ctrl = attachGetters(StrictObj(this))
  $ctrl.wrapH = DefineHandler(sign)
  $ctrl.wrapM = DefineMethod(sign)

  const propertiesEmitter = SumEmitter(
    $ctrl.ggsProperties.key2emitter.locale,
    $ctrl.ggsProperties.key2emitter.custom_styles,
    $ctrl.ggsProperties.key2emitter.body_font_name,
    $ctrl.ggsProperties.key2emitter.show_vote_count,
    $ctrl.ggsProperties.key2emitter.contestant_login,
    $ctrl.ggsProperties.key2emitter.complete_message_bundle)

  const iframeParentElm = domh.find($element, QS_IFRAME_PARENT)
  const spinnerElm = domh.find($element, QS_SPINNER)
  const voteFrame = VoteFrame($ctrl.ggsProperties.vote_frame_path, iframeParentElm)

  $ctrl.wrapH('$onInit', () => {
    $scope.$watch($ctrl.getters.ggsPost.terminate, $ctrl.postChangeHandler)
    propertiesEmitter.on($ctrl.propertiesChangeHandler)
    voteFrame.message.on($ctrl.messageHandler)
    reinstanceSignalEmitter.on($ctrl.reinstanceSignalHander)

    return qh.join(
      animate.showForce(spinnerElm),
      animate.hideForce(iframeParentElm),
      voteFrame.init().then(qh.makeJoin(
        $ctrl.emitItemChange,
        $ctrl.emitPropertiesChange,
        animate.makeHide(spinnerElm),
        animate.makeShow(iframeParentElm))),
      $ctrl.ggsVoteFrameAttachCallback($ctrl.extVoteFrameHidePopups))
  })

  $ctrl.wrapH('$onDestroy', () => {
    propertiesEmitter.off($ctrl.propertiesChangeHandler)
    voteFrame.message.off($ctrl.messageHandler)
    reinstanceSignalEmitter.off($ctrl.reinstanceSignalHander)
    return voteFrame.destroy()
  })

  $ctrl.wrapM('extVoteFrameHidePopups', () => {
    return voteFrame.emit(PARENT_HIDE_POPUPS_REQUEST)
  })

  $ctrl.wrapM('emitItemChange', () => {
    if ($ctrl.ggsPost) {
      return voteFrame.emit(PARENT_ITEM_CHANGE, {
        itemId: $ctrl.ggsPost.id,
        votesCount: $ctrl.ggsPost.votes_count
      })
    }
  })

  $ctrl.wrapM('emitPropertiesChange', () => {
    return voteFrame.emit(PARENT_PROPERTIES_CHANGE, $ctrl.ggsProperties)
  })

  $ctrl.wrapH('reinstanceSignalHander', (peerSign) => {
    if (peerSign !== sign) {
      return voteFrame.emit(PARENT_REINSTANCE_SIGNAL)
    }
  })

  $ctrl.wrapH('postChangeHandler', $ctrl.emitItemChange)
  $ctrl.wrapH('propertiesChangeHandler', $ctrl.emitPropertiesChange)

  const namespace2action = {
    [FRAME_VOTE_CHANGE]: callVoteChangeHandler,
    [FRAME_VOTE_COUNT_HIDE]: callVoteCountHideHandler,
    [FRAME_VOTING_DISALLOW]: callVotingDisallowHandler,
    [FRAME_KEYDOWN_TRIGGER]: simulateKeyDown,
    [FRAME_REINSTANCE_SIGNAL]: emitReinstanceSignal,
    [FRAME_LEFTCLICK_TRIGGER]: simulateLeftClick,
    [FRAME_SEND_DATA_REQUEST]: over($ctrl.emitItemChange, $ctrl.emitPropertiesChange)
  }

  $ctrl.wrapH('messageHandler', (namespace, payload) => {
    const action = namespace2action[namespace]
    if (!action) {
      throw new Error(`${sign}#messageHandler: unknown [namespace=${namespace}]!`)
    }
    return action(payload)
  })

  function callVoteChangeHandler({
    itemId,
    isVoted,
    votesCount
  }) {
    if ($ctrl.ggsPost && $ctrl.ggsPost.id === itemId) {
      return $ctrl.ggsVoteChangeHandler($ctrl.ggsPost, isVoted, votesCount)
    }
  }

  function callVoteCountHideHandler() {
    return $ctrl.ggsVoteCountHideHandler()
  }

  function callVotingDisallowHandler() {
    return $ctrl.ggsVotingDisallowHandler()
  }

  function emitReinstanceSignal() {
    reinstanceSignalEmitter.emit(sign)
  }

  function simulateKeyDown(keyCode) {
    return simkey(document, keyCode)
  }

  function simulateLeftClick() {
    simulateEvent.simulate(document.body, 'mousedown')
    simulateEvent.simulate(document.body, 'mouseup')
    simulateEvent.simulate(document.body, 'click')
  }
}
